/**
 * Per-route SEO head model (#103, FR-15.3) — the single, framework-free source of
 * truth for what a route's `<head>` should contain. Both consumers derive from here so
 * the client hook and the build-time prerender can never drift:
 *
 *   - {@link useDocumentHead} imperatively upserts these tags on the live `document`
 *     (client-side, per-route);
 *   - {@link renderHeadHtml} serializes the same set to an HTML string that the
 *     prerender step ({@link ../prerender-entry}) bakes into `dist/index.html` so
 *     crawlers/link-preview bots get real metadata without executing JS (FR-15.2).
 *
 * Pure and DOM-free so it unit-tests directly and runs under Node SSR.
 */

/** Canonical production origin (D103.8). Canonical + sitemap URLs reference this host. */
export const PROD_ORIGIN = 'https://nance.games';

/** Public path of the Open Graph / Twitter share image (1200×630, `public/`-served). */
export const OG_IMAGE_PATH = '/og-image.png';

/** A JSON-LD node (or array of nodes) for structured data. */
export type JsonLd = Record<string, unknown> | Record<string, unknown>[];

/** Everything a route contributes to `<head>` for SEO + social sharing. */
export interface DocumentHead {
  /** Full `<title>` text (already brand-interpolated by the caller). */
  title: string;
  /** `<meta name="description">` + `og:description`. */
  description: string;
  /** Route path (leading `/`) → resolved to an absolute canonical URL on {@link PROD_ORIGIN}. */
  canonicalPath: string;
  /** Optional share image path/URL → `og:image` + `twitter:image` (upgrades the card). */
  image?: string;
  /** Optional structured data emitted as `<script type="application/ld+json">`. */
  jsonLd?: JsonLd;
  /**
   * Indexing directive (#241, FR-15.4). `'noindex'` emits `<meta name="robots"
   * content="noindex">` so app-only surfaces (`/profile`, `/presentation`) and dynamic
   * game routes stay out of the index even for JS-capable crawlers. Omitted (or
   * `'index'`) leaves the route indexable — no `robots` meta is emitted (index is the
   * default), matching the prior head output.
   */
  robots?: 'index' | 'noindex';
}

/** Resolve a route path to its absolute canonical URL on the production host. */
export function canonicalUrl(canonicalPath: string): string {
  const path = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  return `${PROD_ORIGIN}${path}`;
}

/** Absolutize a possibly-relative image path against the production origin. */
export function absoluteImageUrl(image: string): string {
  return /^https?:\/\//.test(image) ? image : `${PROD_ORIGIN}${image}`;
}

/** A single `<meta>` descriptor: keyed either by `name` (SEO/Twitter) or `property` (OG). */
export interface MetaTag {
  name?: string;
  property?: string;
  content: string;
}

/**
 * The full ordered `<meta>` set for a head model: description, Open Graph, and Twitter
 * card. When an {@link DocumentHead.image} is present the Twitter card is upgraded to
 * `summary_large_image` and `og:image`/`twitter:image` are emitted (FR-15.3).
 */
export function metaTagsFor(head: DocumentHead): MetaTag[] {
  const canonical = canonicalUrl(head.canonicalPath);
  const tags: MetaTag[] = [
    { name: 'description', content: head.description },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: head.title },
    { property: 'og:description', content: head.description },
    { property: 'og:url', content: canonical },
    { name: 'twitter:title', content: head.title },
    { name: 'twitter:description', content: head.description },
  ];
  if (head.image) {
    const img = absoluteImageUrl(head.image);
    tags.push(
      { property: 'og:image', content: img },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: img },
    );
  } else {
    tags.push({ name: 'twitter:card', content: 'summary' });
  }
  // Fine-grained noindex (#241, FR-15.4) — only emitted when the route opts out;
  // an indexable route emits no `robots` meta (index is the crawler default).
  if (head.robots === 'noindex') {
    tags.push({ name: 'robots', content: 'noindex' });
  }
  return tags;
}

/**
 * Serialize a JSON-LD node to a `<script type="application/ld+json">` element for the
 * prerender bake (FR-15.2). The payload is `</`-escaped so structured-data content can
 * never break out of the script element. The landing's title/description/canonical/OG
 * live statically in `index.html`; this adds the one head element that must be generated
 * from the typed model.
 *
 * The `data-managed-head` marker matches {@link ./useDocumentHead}, so when the client
 * mounts over the prerendered page the hook UPDATES this node in place rather than
 * appending a second structured-data block.
 */
export function renderJsonLdScript(jsonLd: JsonLd): string {
  const json = JSON.stringify(jsonLd).replace(/</g, '\\u003c');
  return `<script type="application/ld+json" data-managed-head>${json}</script>`;
}

/** HTML-attribute-escape a value so route strings can't break out of an attribute. */
function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** HTML-text-escape a value for element content (the `<title>`). */
function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Serialize a full head model to the `<head>` HTML the multi-route prerender bakes into
 * each route's `dist/<route>/index.html` (#241, FR-15.4) — the serializer this module's
 * docstring has promised since #103. Emits `<title>`, the {@link metaTagsFor} set, the
 * self-referencing `<link rel="canonical">`, and (when present) the JSON-LD via
 * {@link renderJsonLdScript}. Every element carries `data-managed-head`, matching the
 * client hook ({@link ./useDocumentHead}) so when the SPA mounts over the prerendered page
 * it UPDATES these nodes in place instead of appending duplicates.
 *
 * Pure + DOM-free: it runs under Node SSR and unit-tests directly.
 */
export function renderHeadHtml(head: DocumentHead): string {
  const parts: string[] = [`<title data-managed-head>${escapeText(head.title)}</title>`];
  for (const tag of metaTagsFor(head)) {
    const key = tag.name
      ? `name="${escapeAttr(tag.name)}"`
      : `property="${escapeAttr(tag.property!)}"`;
    parts.push(`<meta ${key} content="${escapeAttr(tag.content)}" data-managed-head />`);
  }
  parts.push(
    `<link rel="canonical" href="${escapeAttr(canonicalUrl(head.canonicalPath))}" data-managed-head />`,
  );
  if (head.jsonLd) parts.push(renderJsonLdScript(head.jsonLd));
  return parts.join('\n    ');
}
